import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { LinkedInParser, PdfResumeParser } from "@reactive-resume/parser";
import {
  Button,
  DialogFooter,
  FileUpload,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Tabs,
  TabsList,
  TabsTrigger,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
} from "@reactive-resume/ui";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z, ZodError } from "zod";
import { cn } from "@reactive-resume/utils";

import { useToast } from "@/client/hooks/use-toast";
import { useDialog } from "@/client/stores/dialog";
import { ImportType, ValidationResult } from "@/client/pages/dashboard/resumes/_dialogs/import";
import { dataImportTypeSchema, Pdf } from "@reactive-resume/utils";
import { Briefcase, ClipboardText, Link, FileText, Plus } from "@phosphor-icons/react";
import { useUser } from "@/client/services/user";
import { useResumes } from "@/client/services/resume";
import type { ResumeDto } from "@reactive-resume/dto";

const importFormSchema = z.object({
  file: z.instanceof(File).optional(),
  type: dataImportTypeSchema,
  selectedResumeId: z.string().optional(),
});

type ImportFormValues = z.infer<typeof importFormSchema>;

enum ImportStep {
  IMPORT_RESUME = 0,
  JOB_DESCRIPTION = 1,
}

enum ImportMode {
  UPLOAD = "upload",
  SELECT_EXISTING = "select_existing",
}

type Props = {
  setCurrentStep: (step: ImportStep) => void;
  validationResult: ValidationResult | null;
  setValidationResult: (result: ValidationResult | null) => void;
  isValidating: boolean;
  setIsValidating: (validating: boolean) => void;
  onFileUploaded: (file: File, type: ImportType) => void;
  onResumeSelected: (resume: ResumeDto) => void;
};

export const StepUploadOrSelect = ({
  setCurrentStep,
  validationResult,
  setValidationResult,
  isValidating,
  setIsValidating,
  onFileUploaded,
  onResumeSelected,
}: Props) => {
  const { toast } = useToast();
  const { user } = useUser();
  const { resumes, loading: resumesLoading } = useResumes(!user?.isAnonymous);

  const [importMode, setImportMode] = useState<ImportMode>(ImportMode.SELECT_EXISTING);
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null);

  const importForm = useForm<ImportFormValues>({
    defaultValues: {
      type: dataImportTypeSchema.enum["pdf-resume"],
    },
    resolver: zodResolver(importFormSchema),
  });
  const filetype = importForm.watch("type");

  // Restore file when validation result exists (when going back to this step)
  useEffect(() => {
    if (validationResult && validationResult.isValid && validationResult.result) {
      // If we have a valid result but no file in the form, it means we're coming back to this step
      const currentFile = importForm.getValues("file");
      if (!currentFile && validationResult.type) {
        // We can't restore the exact File object, but we can set the form to show the validated state
        importForm.setValue("type", validationResult.type);
      }
    }
  }, [validationResult, importForm]);

  useEffect(() => {
    // Only reset if we don't have a valid validation result for the current file type
    const shouldReset =
      !validationResult || !validationResult.isValid || validationResult.type !== filetype;

    if (shouldReset) {
      importForm.reset({ file: undefined, type: filetype, selectedResumeId: undefined });
      setValidationResult(null);
      setSelectedResumeId(null);
    }
  }, [filetype, validationResult, importForm, setValidationResult]);

  const accept = useMemo(() => {
    if (filetype.includes("json")) return ".json";
    if (filetype.includes("zip")) return ".zip";
    if (filetype.includes("pdf")) return ".pdf";
    return "";
  }, [filetype]);

  const onValidate = async (file: File, fileType: ImportType) => {
    try {
      setIsValidating(true);

      console.log("Starting validation for file:", file.name, "type:", fileType);

      if (fileType === dataImportTypeSchema.enum["linkedin-data-export-zip"]) {
        const parser = new LinkedInParser();
        const data = await parser.readFile(file);
        const result = await parser.validate(data);

        setValidationResult({ isValid: true, type: fileType, result });
      }

      if (fileType === dataImportTypeSchema.enum["pdf-resume"]) {
        const parser = new PdfResumeParser();
        const data = await parser.readFile(file);
        const result = (await parser.validate(data)) as unknown as Pdf;
        setValidationResult({ isValid: true, type: fileType, result });
      }
    } catch (error: unknown) {
      if (error instanceof ZodError) {
        setValidationResult({
          isValid: false,
          errors: error.toString(),
        });

        toast({
          variant: "error",
          title: t`An error occurred while validating the file.`,
        });
      }
      setValidationResult({
        isValid: false,
        errors:
          error instanceof Error ? error.message : "An error occurred while validating the file.",
      });
      console.error(error);
    } finally {
      setIsValidating(false);
    }
  };

  const getErrorMessage = () => {
    if (validationResult !== null && !validationResult.isValid) {
      if (validationResult.errors) {
        return validationResult.errors;
      }
      return "Something went wrong. Please try again.";
    }
    return "";
  };

  const handleResumeSelect = (resume: ResumeDto) => {
    setSelectedResumeId(resume.id);
    onResumeSelected(resume);
    setCurrentStep(ImportStep.JOB_DESCRIPTION);
  };

  const showExistingResumes = !user?.isAnonymous && resumes && resumes.length > 0;

  const tabClasses =
    "p-3 flex flex-col w-1/2 text-gray-500 cursor-pointer px-4 text-[13px] [&>span]:leading-none [&>span]:whitespace-normal gap-3 [&>svg]:h-8 [&>svg]:w-8 [&>svg]:text-gray-500" +
    "[data-state=active]:bg-blue-500 [&[data-state=active]>*]:text-black";

  return (
    <Form {...importForm}>
      <div className="space-y-6">
        {/* Mode Selection - Only show if user has existing resumes */}
        {showExistingResumes && (
          <div className="space-y-1">
            <div className="text-left">
              <p className="text-[13px] font-semibold text-gray-600">
                {t`Choose how you'd like to improve your resume:`}
              </p>
            </div>
            <Tabs
              value={importMode}
              className="space-y-4"
              onValueChange={(mode) => {
                setImportMode(mode as ImportMode);
                setValidationResult(null);
                setSelectedResumeId(null);
              }}
            >
              <TabsList className="bg-secondary/5 grid w-full grid-cols-2">
                <TabsTrigger value={ImportMode.SELECT_EXISTING} className="flex items-center gap-2">
                  <FileText size={16} />
                  {t`Use Existing Resume`}
                </TabsTrigger>
                <TabsTrigger value={ImportMode.UPLOAD} className="flex items-center gap-2">
                  <Plus size={16} />
                  {t`Upload New Resume`}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* File Upload Section */}
        {(importMode === ImportMode.UPLOAD || !showExistingResumes) && (
          <form className="space-y-5 border-b border-gray-200 pb-4">
            {/* Show previously uploaded file status */}
            {validationResult && validationResult.isValid && (
              <div className="rounded-md border border-green-200 bg-green-50 p-3">
                <div className="flex items-center gap-2 text-green-800">
                  <ClipboardText size={16} />
                  <span className="text-sm font-medium">{t`File successfully validated`}</span>
                </div>
                <p className="mt-1 text-xs text-green-600">
                  {t`Your ${validationResult.type.replace("-", " ")} has been processed. You can upload a different file below if needed.`}
                </p>
              </div>
            )}

            <FormField
              name="file"
              control={importForm.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {validationResult && validationResult.isValid
                      ? t`Upload a different file (optional)`
                      : t`File`}
                  </FormLabel>
                  <FormControl>
                    <FileUpload
                      key={`${accept}-${filetype}`}
                      accept={accept}
                      loading={isValidating}
                      label={t`Choose File`}
                      subtitle={t`or drag and drop`}
                      error={getErrorMessage()}
                      onChange={(file) => {
                        field.onChange(file);
                        const currentType = importForm.getValues("type") as ImportType;

                        onFileUploaded(file, currentType);

                        setTimeout(() => {
                          onValidate(file, currentType);
                        }, 100);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                  {accept && (
                    <FormDescription>
                      {t({
                        message: `Accepts only ${accept} files`,
                        comment:
                          "Helper text to let the user know what filetypes are accepted. {accept} can be .pdf or .json.",
                      })}
                    </FormDescription>
                  )}
                </FormItem>
              )}
            />
          </form>
        )}

        {/* Existing Resumes Selection Section */}
        {importMode === ImportMode.SELECT_EXISTING && showExistingResumes && (
          <div className="space-y-4">
            <div className="space-y-2">
              <FormLabel>{t`Select an existing resume`}</FormLabel>
              <FormDescription>
                {t`Choose one of your existing resumes as a starting point`}
              </FormDescription>
            </div>
            {resumesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-500">{t`Loading your resumes...`}</div>
              </div>
            ) : (
              <div className="grid max-h-80 gap-3 overflow-y-auto pr-2">
                {resumes.map((resume) => (
                  <Card
                    key={resume.id}
                    className={cn(
                      "border-border cursor-pointer border p-4 shadow-sm transition-all hover:shadow-md",
                      selectedResumeId === resume.id
                        ? "border-primary bg-primary/5"
                        : "border-gray-200 hover:border-gray-300",
                    )}
                    onClick={() => handleResumeSelect(resume)}
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <FileText size={18} className="text-primary" />
                        {resume.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{t`Updated: ${new Date(resume.updatedAt).toLocaleDateString()}`}</span>
                        <Badge
                          variant={resume.visibility === "public" ? "warning" : "primary"}
                          className="capitalize"
                        >
                          {resume.visibility}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Continue button for previously validated files */}
      {validationResult &&
        validationResult.isValid &&
        (importMode === ImportMode.UPLOAD || !showExistingResumes) && (
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setCurrentStep(ImportStep.JOB_DESCRIPTION)}
              className="w-full"
            >
              {t`Continue with validated file`}
            </Button>
          </DialogFooter>
        )}
      <div className="w-full items-center justify-center text-center text-xs opacity-50">{t`Free forever • No credit card required • 14 days free trial`}</div>
    </Form>
  );
};
